import { Badge, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { Accordion, ButtonText } from '@morressier/alembic';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBuildingColumns,
  faEdit,
  faEnvelope,
  faGraduationCap,
  faTrash
} from '@fortawesome/pro-regular-svg-icons';
import { ISearchQueryItems } from 'src/api/queries';
import { ILexiconField } from '@publishing-engine/domain/models';
import {
  getReviewerPropertyValue,
  isJournalManagerPartialUser,
  isJournalManagerUser
} from 'src/utils/reviews';
import { APPLICATION_NAME } from 'src/constants';
import SubmittedAuthorDetailsModal from 'src/components/Submission/AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import ReviewerTagBadges from 'src/components/Submission/ReviewTab/ReviewerSearch/ReviewerTagBadges';
import ReviewerPropertyRow from 'src/components/Submission/ReviewTab/ReviewerSearch/ReviewerPropertyRow';
import ReviewerMetricsGrid from 'src/components/Submission/ReviewTab/ReviewerSearch/ReviewerMetricsGrid';
import LabelWithTooltip from 'src/components/Submission/ReviewTab/ReviewerSearch/LabelWithTooltip';
import { ExpandableText } from 'src/components/Submission/ReviewTab/ReviewerSearch/ExpandableText';
import { getPropertyStrings } from './types';

interface Props {
  item: ISearchQueryItems;
  reviewerConfig?: ILexiconField[];
  expandAll?: boolean;
  onEdit: (item: ISearchQueryItems) => void;
  onDelete: (item: ISearchQueryItems) => void;
}

const ManageReviewerResult = ({
  item,
  reviewerConfig,
  expandAll,
  onEdit,
  onDelete
}: Props) => {
  const [profileUserId, setProfileUserId] = useState<string | null>(null);

  const getConfig = (predicateLabel: string) =>
    reviewerConfig?.find(field => field.predicateLabel === predicateLabel);

  const displayName =
    getPropertyStrings(item.properties, 'ReviewerDisplayname')?.[0] ?? '-';
  const reviewerUserId = getReviewerPropertyValue(
    item.properties,
    'ReviewerUserID'
  );
  const isJournalManager = isJournalManagerUser(item);
  const isBoardMember =
    item.properties.some(p => p.propertyName === 'boardMember') &&
    item.properties.find(p => p.propertyName === 'boardMember')
      ?.propertyBoolean === true;
  const userTags = getPropertyStrings(item.properties, 'userTag') ?? [];
  const emailValues = getPropertyStrings(
    item.properties,
    'ReviewerPrimaryEmailAddress'
  );
  const degreeValues = getPropertyStrings(item.properties, 'ReviewerDegree');
  const specialtyValues = getPropertyStrings(
    item.properties,
    'ReviewerSpecialty'
  );
  const subSpecialtyValues = getPropertyStrings(
    item.properties,
    'ReviewerSubSpecialty'
  );
  const keywordValues = getPropertyStrings(item.properties, 'ReviewerKeyword');
  const scandinavianValues = getPropertyStrings(
    item.properties,
    'scandinavianKeywords'
  );

  const institutionConfig = getConfig('ReviewerInstitution');
  const degreeConfig = getConfig('ReviewerDegree');
  const specialtyConfig = getConfig('ReviewerSpecialty');
  const subSpecialtyConfig = getConfig('ReviewerSubSpecialty');
  const keywordConfig = getConfig('ReviewerKeyword');
  const scandinavianConfig = getConfig('scandinavianKeywords');

  const expertiseFields = [
    { config: specialtyConfig, values: specialtyValues },
    { config: subSpecialtyConfig, values: subSpecialtyValues },
    { config: keywordConfig, values: keywordValues },
    { config: scandinavianConfig, values: scandinavianValues }
  ].flatMap(field =>
    field.config?.reviewerCard === true
      ? [{ config: field.config, values: field.values }]
      : []
  );

  const othersFields = [
    'ReviewerSecondaryEmailAddress',
    'ReviewerPosition',
    'ReviewerPeopleNotes',
    'ReviewerDetailedPeopleNotes',
    'ReviewerQualifications',
    'ReviewerOrigin',
    'ReviewerInterests',
    'ReviewerGenericNotes',
    'ReviewerBiography'
  ]
    .map(key => ({ key, config: getConfig(key) }))
    .filter(
      (field): field is { key: string; config: ILexiconField } =>
        field.config !== undefined && field.config.reviewerCard === true
    );


  const collapsedMetrics = [
        'AverageRScore',
    'ActiveReviews',
    'NumberOfTimesSubmitted',
    'AverageReviewerTurnaroundTime',
    'ReviewsInvited',
    'ReviewsNoReply',
    'ReviewsDeclined',
    'TimesUninvited',
    'TimesSelected',
    'DaysSinceLastReview',
    'LastInvitedDate',
    'PercentAcceptedNoReview',
    'PercentInvitedNoReview',
    'ReviewerOnboardedDate',
    'AcceptedNoReview',
    'Agreed',
    'ReviewerPersonDesignation',
    'TotalOpenInvites',
    'LastSubmittedDate'
  ]
    .map(key => ({ key, config: getConfig(key) }))
    .filter(
      (metric): metric is { key: string; config: ILexiconField } =>
        metric.config !== undefined && metric.config.reviewerCard === true
    );

  return (
    <>
      {profileUserId && (
        <SubmittedAuthorDetailsModal
          userId={profileUserId}
          onBackClick={() => setProfileUserId(null)}
          backButtonText="Back to Reviewer Search"
        />
      )}
      <Accordion
        key={expandAll ? 'expanded' : 'collapsed'}
        chakraAccordionProps={{
          allowMultiple: true,
          defaultIndex: expandAll ? [0] : undefined,
          width: '100%',
          background: 'surfacePrimaryFill'
        }}
        items={[
          {
            title: (
              <Flex flexDirection="row" gap={2} alignItems="center">
                <Text textStyle="headingItem">{displayName}</Text>
                {isJournalManager ? (
                  <Badge variant="solid">
                    <Text textStyle="textContext">{APPLICATION_NAME} User</Text>
                  </Badge>
                ) : null}
                {isBoardMember ? (
                  <Badge variant="solid">
                    <Text textStyle="textContext">Board Member</Text>
                  </Badge>
                ) : null}
              </Flex>
            ),
            content: (
              <Flex flexDirection="column" mb={2} gap={1}>
                <Flex justifyContent="flex-end" gap={2}>
                  {isJournalManagerPartialUser(item) &&
                    reviewerUserId !== '-' && (
                      <ButtonText
                        size="regular"
                        iconLeft={
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        }
                        onClick={() => setProfileUserId(reviewerUserId)}
                      >
                        View Profile
                      </ButtonText>
                    )}
                  {!isJournalManager && (
                    <>
                      <ButtonText
                        variant="primary"
                        iconLeft={<FontAwesomeIcon icon={faEdit} />}
                        onClick={() => onEdit(item)}
                      >
                        Edit
                      </ButtonText>
                      <ButtonText
                        variant="caution"
                        iconLeft={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => onDelete(item)}
                      >
                        Delete
                      </ButtonText>
                    </>
                  )}
                </Flex>

                <ReviewerTagBadges tags={userTags} />
                {institutionConfig &&
                  institutionConfig.reviewerCard === true && (
                    <ReviewerPropertyRow
                      config={institutionConfig}
                      icon={faBuildingColumns}
                      values={
                        [
                          getPropertyStrings(
                            item.properties,
                            'ReviewerInstitution'
                          )?.[0],
                          getPropertyStrings(
                            item.properties,
                            'ReviewerDepartment'
                          )?.[0],
                          getPropertyStrings(
                            item.properties,
                            'ReviewerCountryOrRegion'
                          )?.[0]
                        ].filter(Boolean) as string[]
                      }
                      marginLeft={'1'}
                    />
                  )}
                <Flex
                  gap={4}
                  wrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Flex alignItems="center" gap={1}>
                    <FontAwesomeIcon icon={faEnvelope} />
                    <Text textStyle="textContext" fontWeight="600">
                      Email:
                    </Text>
                    <Text
                      textStyle="textContext"
                      whiteSpace="normal"
                      overflowWrap="anywhere"
                      wordBreak="break-word"
                    >
                      {emailValues?.length ? emailValues.join(', ') : '-'}
                    </Text>
                  </Flex>
                </Flex>

                {degreeConfig && degreeConfig.reviewerCard === true && (
                  <ReviewerPropertyRow
                    config={degreeConfig}
                    icon={faGraduationCap}
                    values={degreeValues}
                    marginLeft={'1'}
                  />
                )}

                <Grid templateColumns="repeat(1, 1fr)" gap={1}>
                  {expertiseFields.map(field => (
                    <Flex
                      textStyle="textContext"
                      key={field?.config?.predicateLabel}
                    >
                      <LabelWithTooltip config={field?.config} />:
                      <Text
                        textStyle="textContext"
                        ml={1}
                        wordBreak="break-word"
                      >
                        {field?.values?.length
                          ? field?.values?.join(', ')
                          : '-'}
                      </Text>
                    </Flex>
                  ))}
                </Grid>

                <Grid templateColumns="repeat(1, 1fr)" gap={0.5}>
                  {othersFields.map(({ key, config }) => {
                    const journalValue = getReviewerPropertyValue(
                      item.properties,
                      key
                    );
                    return (
                      <GridItem
                        key={key}
                        textStyle="textContext"
                        display="flex"
                        alignItems="stretch"
                        gap={1}
                      >
                        <LabelWithTooltip config={config} /> :
                        <Flex alignItems="start" gap={2}>
                          <Flex alignItems="center" gap={1}>
                            <ExpandableText text={journalValue} />
                          </Flex>
                        </Flex>
                      </GridItem>
                    );
                  })}
                </Grid>

                <ReviewerMetricsGrid
                  item={item}
                  visibleMetrics={[]}
                  collapsedMetrics={collapsedMetrics}
                  globalLexiconConfig={false}
                />
              </Flex>
            )
          }
        ]}
      />
    </>
  );
};

export default ManageReviewerResult;
